import { nanoid } from "nanoid";
import { ExcelEventEmitter, NamedItem, NamedRange, Table, TableColumn, WorkbookProperties } from "./excelEvents";

const workbookPropsToExtract = ["title","author","manager","comments","category","company","revisionNumber","subject","lastAuthor"];

export class ExcelEventRelay {
    private taskpaneOpen: boolean = true;
    private workbookId: string | null = null;

    constructor(private appEventEmitter: ExcelEventEmitter) { }

    async start() {
        if (Office.context.host !== Office.HostType.Excel) return;
        Office.addin.onVisibilityModeChanged(this.onTaskpaneVisibilityChanged);
        window.addEventListener('beforeunload', this.onWorkbookClosed);

        const x = Office.context.document.settings.remove("some-key");
        Office.context.document.settings.saveAsync();

        const workbookData = await this.getWorkbookData();
        const {activeWorksheet, ...workbookOpenedEvent} = workbookData;
        this.workbookId = workbookData.id;

        this.appEventEmitter.events.workbookOpened.emit({
            workbookId: workbookData.id,
            workbook: workbookOpenedEvent
        });
        this.appEventEmitter.events.workbookActivated.emit({workbookId: workbookData.id});
        this.appEventEmitter.events.worksheetActivated.emit({
            workbookId: workbookData.id,
            worksheet: { id: activeWorksheet.id, name: activeWorksheet.name }
        });
    }

    private async getWorkbookData() {
        const existingWorkbookId:string = Office.context.document.settings.get("workbookId");
        const workbookId = existingWorkbookId ?? nanoid();
        if (!existingWorkbookId) {
            Office.context.document.settings.set("workbookId", workbookId);
            Office.context.document.settings.saveAsync();
        }

        return await Excel.run(async (context) => {
            //TODO: load or set a workbook ID
            const url = Office.context.document.url;

            const workbook = context.workbook;
            workbook.load("name");

            const workbookProperties = workbook.properties;
            workbookProperties.load(workbookPropsToExtract);

            const workbookNamedItems = workbook.names;
            workbookNamedItems.load("items/name,items/scope,items/type");
            
            const workbookTables = workbook.tables;
            workbookTables.load("items/name,items/columns/items/name,items/columns/items/index");

            const workbookLinkedWorkbooks = workbook.linkedWorkbooks;
            workbookLinkedWorkbooks.load();

            const workbookWorksheets = context.workbook.worksheets;
            workbookWorksheets.load("items/name,items/names,items/namedSheetViews");

            const workbookActiveWorksheet = workbookWorksheets.getActiveWorksheet();
            workbookActiveWorksheet.load("name");
            await context.sync();
            
            const name = workbook.name;
            const properties = workbookPropsToExtract.reduce((acc, prop) => {
                acc[prop] = workbookProperties[prop];
                return acc;
            }, {} as WorkbookProperties);

            const workbookNamedRanges = workbookNamedItems.items.filter(namedItem => namedItem.type === "Range").reduce<Record<string,Excel.Range>>(
                (acc, namedRange) => {
                    acc[namedRange.name] = namedRange.getRange().load("address");
                    return acc;
                },
                {}
            );
            const worksheetNamedItems = workbookWorksheets.items.reduce<Record<string, Excel.NamedItemCollection>>((acc, worksheet) => {
                acc[worksheet.name] = worksheet.names.load("items/name,items/scope,items/type");
                return acc;
            }, {});
            await context.sync();

            const tables = workbookTables.items.map<Table>(table => ({
                id: table.id,
                name: table.name,
                columns: table.columns.items.map<TableColumn>(column => ({ id: column.id.toString(), name: column.name, index: column.index }))
            }));

            const linkedWorkbooks = workbookLinkedWorkbooks.items.map(link => link.id);
            const namedItems:(NamedItem|NamedRange)[] = workbookNamedItems.items.map<NamedItem>(namedItem => {
                const result = { name: namedItem.name, scope: namedItem.scope, type: namedItem.type };
                return namedItem.type === "Range"
                    ? { ... result, address: workbookNamedRanges[namedItem.name].address }
                    : result;
            });

            const worksheetNamedRanges = Object.entries(worksheetNamedItems).reduce<Record<string, Record<string, Excel.Range>>>((acc, [worksheetName, namedItemCollection]) => {
                    acc[worksheetName] = namedItemCollection.items.filter(namedItem => namedItem.type === "Range").reduce<Record<string,Excel.Range>>(
                        (acc, namedRange) => {
                            acc[namedRange.name] = namedRange.getRange().load("address");
                            return acc;
                        },
                        {}
                    );
                    return acc;
            }, {});
            await context.sync();

            const worksheets = workbookWorksheets.items.map((sheet) => {
                const namedItems:(NamedItem|NamedRange)[] = worksheetNamedItems[sheet.name].items.map<NamedItem>(namedItem => {
                    const result = { name: namedItem.name, scope: namedItem.scope, type: namedItem.type };
                    return namedItem.type === "Range"
                        ? { ... result, address: worksheetNamedRanges[sheet.name][namedItem.name].address }
                        : result;
                });

                return {
                    id: sheet.id,
                    name: sheet.name,
                    namedItems
                };
            });

            const activeWorksheet = {
                id: workbookActiveWorksheet.id,
                name: workbookActiveWorksheet.name
            };

            workbook.onActivated.add(() => this.onWorkbookActivated());
            workbook.onSelectionChanged.add(() => this.onSelectionChanged());
            workbookWorksheets.onActivated.add((args) => this.onWorksheetActivated(args));
            workbookWorksheets.onAdded.add((args) => this.onWorksheetAdded(args));
            workbookWorksheets.onChanged.add((args) => this.onWorksheetChanged(args));
            workbookWorksheets.onDeleted.add((args) => this.onWorksheetDeleted(args));
            workbookWorksheets.onNameChanged.add((args) => this.onWorksheetNameChanged(args));
            workbookWorksheets.onCalculated.add((args) => this.onWorksheetCalculated(args));

            return {
                id: workbookId,
                url,
                name,
                properties,
                linkedWorkbooks,
                namedItems,
                tables,
                worksheets,
                activeWorksheet
            }
        });
    }


    private onTaskpaneVisibilityChanged(args: Office.VisibilityModeChangedMessage) {
        const visible = args.visibilityMode === Office.VisibilityMode.taskpane;
        const changed = this.taskpaneOpen !== visible;
        this.taskpaneOpen = visible;
        if (changed) {
            if (visible) this.appEventEmitter.events.taskpaneOpened.emit({source: "onVisibilityModeChanged"});
            else this.appEventEmitter.events.taskpaneClosed.emit({source: "onVisibilityModeChanged"});
        }
    }
    private onWorkbookClosed() {
        if (this.appEventEmitter) this.appEventEmitter.events.workbookClosed.emit({workbookId: this.workbookId});
    }
    private async onWorkbookActivated() { this.appEventEmitter.events.workbookActivated.emit({workbookId: this.workbookId}); }
    private async onWorksheetActivated(args: Excel.WorksheetActivatedEventArgs) {
        Excel.run(async (ctx) => {
            const worksheet = ctx.workbook.worksheets.getItem(args.worksheetId);
            worksheet.load("name");
            await ctx.sync();

            this.appEventEmitter.events.worksheetActivated.emit({workbookId: this.workbookId, worksheet:{id: worksheet.id, name: worksheet.name}});
        });
    }
    private async onWorksheetAdded(args: Excel.WorksheetAddedEventArgs) {
        Excel.run(async (ctx) => {
            const addedWorksheet = ctx.workbook.worksheets.getItem(args.worksheetId);
            addedWorksheet.load("name,names,namedSheetViews");
            await ctx.sync();
            const worksheetNamedItems = addedWorksheet.names.load("items/name,items/scope,items/type");
            await ctx.sync();

            const namedRanges = worksheetNamedItems.items.filter(namedItem => namedItem.type === "Range").reduce<Record<string,Excel.Range>>(
                (acc, namedRange) => {
                    acc[namedRange.name] = namedRange.getRange().load("address");
                    return acc;
                },
                {}
            );
            await ctx.sync();

            const namedItems:(NamedItem|NamedRange)[] = worksheetNamedItems.items.map<NamedItem>(namedItem => {
                const result = { name: namedItem.name, scope: namedItem.scope, type: namedItem.type };
                return namedItem.type === "Range"
                    ? { ... result, address: namedRanges[namedItem.name].address }
                    : result;
            });

            this.appEventEmitter.events.worksheetAdded.emit({workbookId: this.workbookId, worksheet:{
                id: addedWorksheet.id,
                name: addedWorksheet.name,
                namedItems
            }});
        });
    }
    private async onSelectionChanged() {
        Excel.run(async (ctx) => {
            const ranges = ctx.workbook.getSelectedRanges();
            ranges.load(["address","worksheet"]);
            ranges.worksheet.load("id");
            await ctx.sync();
            
            this.appEventEmitter.events.selectionChanged.emit({workbookId: this.workbookId, worksheetId: ranges.worksheet.id, rangeAddress: ranges.address});
        });
    }
    private async onWorksheetChanged(args: Excel.WorksheetChangedEventArgs) {
        this.appEventEmitter.events.worksheetChanged.emit({workbookId: this.workbookId, worksheetId: args.worksheetId, excelEvent: args});
    }
    private async onWorksheetDeleted(args: Excel.WorksheetDeletedEventArgs) {
        this.appEventEmitter.events.worksheetDeleted.emit({workbookId: this.workbookId, worksheetId: args.worksheetId});
    }
    private async onWorksheetNameChanged(args: Excel.WorksheetNameChangedEventArgs) {
        this.appEventEmitter.events.worksheetNameChanged.emit({workbookId: this.workbookId, worksheetId: args.worksheetId, from: args.nameBefore, to: args.nameAfter});
    }
    private async onWorksheetCalculated(args: Excel.WorksheetCalculatedEventArgs) {
        this.appEventEmitter.events.calculationCompleted.emit({workbookId: this.workbookId, worksheetId: args.worksheetId, address: args.address});
    }
}